import React, { useState, useEffect, useMemo } from 'react';
import { GlobalButton } from '../GlobalButton';
import { GlobalInput } from '../GlobalInput';
import { GlobalTextarea } from '../GlobalTextarea';
import { GlobalDropdown } from '../GlobalDropdown';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import { FunctionalSpecCard } from './data/FunctionalSpecCard';
import styles from './FSTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useTheme } from '../../context/ThemeProvider';
import { FilterBar } from '../FilterBar';
import { FunctionalSpecification } from '../../types';
import { createFunctionalSpec } from '../../services/domainEntities';
import GlobalEmptyState from '../GlobalEmptyState';
import GlobalSidebar from '../GlobalSidebar';
import { URSAutoComplete } from '../URSAutoComplete';
import { TestAutoComplete } from '../TestAutoComplete';
import { RiskAutoComplete } from '../RiskAutoComplete';
import { SOPAutoComplete } from '../SOPAutoComplete';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUser } from '../../context/UserContext';
import DoubleConfirmationModal from '../DoubleConfirmationModal';
import EntityGenerationSidebar from '../EntityGenerationSidebar';
import TransformEntitiesSidebar from '../TransformEntitiesSidebar';
import AddDocumentSidebar from '../AddDocumentSidebar';
import { UserRequirement } from '../../types';
import { exportToCSV } from '../../utils/exportToCSV';

interface FSTabProps {
  projectId: string;
  onExport?: () => void;
  onNavigateToEntity?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
}

export const FSTab: React.FC<FSTabProps> = ({ projectId, onExport, onNavigateToEntity }) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  
  const [specs, setSpecs] = useState<FunctionalSpecification[]>([]);
  const [selectedSpec, setSelectedSpec] = useState<FunctionalSpecification | null>(null);
  const [showSpecEditor, setShowSpecEditor] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [specToDelete, setSpecToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEntityGenerationSidebar, setShowEntityGenerationSidebar] = useState(false);
  const [selectedEntitiesForGeneration, setSelectedEntitiesForGeneration] = useState<Array<{
    id: string;
    type: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP';
    title: string;
  }>>([]);
  const [showTransformEntitiesSidebar, setShowTransformEntitiesSidebar] = useState(false);
  const [ursList, setUrsList] = useState<UserRequirement[]>([]);
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);

  useEffect(() => {
    const loadSpecs = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'fs');
        const snapshot = await getDocs(col);
        const loaded: FunctionalSpecification[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: data.id || `FS-${doc.id.substring(0, 8)}`,
            _firestoreId: doc.id
          } as FunctionalSpecification & { _firestoreId: string };
        });
        setSpecs(loaded);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading FS:', err);
        }
        showToast(t('project_tab_spec_load_failed') || 'Failed to load FS', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSpecs();
  }, [projectId, showToast, t]);

  const handleSaveSpec = async (spec: FunctionalSpecification) => {
    try {
      const firestoreId = (spec as any)?._firestoreId;
      
      if (firestoreId) {
        // Update existing
        const docRef = doc(db, 'projects', projectId, 'fs', firestoreId);
        const { _firestoreId, ...dataToSave } = spec as any;
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        } as any);
      } else {
        // Create new
        await createFunctionalSpec(projectId, spec);
      }
      
      // Reload specs from Firestore
      const col = collection(db, 'projects', projectId, 'fs');
      const snapshot = await getDocs(col);
      const loaded: FunctionalSpecification[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: data.id || `FS-${doc.id.substring(0, 8)}`,
          _firestoreId: doc.id
        } as FunctionalSpecification & { _firestoreId: string };
      });
      setSpecs(loaded);
      
      setShowSpecEditor(false);
      setSelectedSpec(null);
      showToast(t('project_tab_spec_saved') || 'Spec saved', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_spec_save_failed') || 'Failed to save spec', 'error');
    }
  };

  const handleAddSpec = () => {
      const newSpec: FunctionalSpecification = {
      id: `FS-${String(specs.length + 1).padStart(3, '0')}`,
      type: 'FS',
      title: '',
      description: '',
      design_hint: '',
      category: 'Application Function',
      gxp_relevance: 'Medium',
      status: 'Draft',
      links: { urs_ids: [], test_ids: [], risk_ids: [], sop_ids: [] },
      meta: { owner: '', tags: [] },
      createdAt: new Date(),
      updatedAt: new Date(),
      inputs: [],
      outputs: [],
      preconditions: [],
      postconditions: []
    };
    setSelectedSpec(newSpec);
    setShowSpecEditor(true);
  };

  // Load URS list for entity generation
  useEffect(() => {
    const loadURS = async () => {
      if (!projectId) return;
      try {
        const col = collection(db, 'projects', projectId, 'urs');
        const snapshot = await getDocs(col);
        const loaded: UserRequirement[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: data.id || `URS-${doc.id.substring(0, 8)}`,
          } as UserRequirement;
        });
        setUrsList(loaded);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading URS:', err);
        }
      }
    };
    loadURS();
  }, [projectId]);

  const handleAiGenerateFromURS = () => {
    // If there are URS, open sidebar with ALL URS selected
    if (ursList.length === 0) {
      showToast(t('project_tab_no_urs_to_generate') || 'No URS available. Please create a URS first.', 'error');
      return;
    }
    // Open with ALL URS selected
    const allURS = ursList.map(urs => ({
      id: urs.id,
      type: 'URS' as const,
      title: urs.title
    }));
    setSelectedEntitiesForGeneration(allURS);
    setShowEntityGenerationSidebar(true);
  };

  const handleAiGenerateFromFS = () => {
    // Open sidebar with ALL FS selected
    if (specs.length === 0) {
      showToast(t('project_tab_no_fs_to_generate') || 'No FS available. Please create a FS first.', 'error');
      return;
    }
    const allFS = specs.map(spec => ({
      id: spec.id,
      type: 'FS' as const,
      title: spec.title
    }));
    setSelectedEntitiesForGeneration(allFS);
    setShowEntityGenerationSidebar(true);
  };

  const handleTransformEntities = () => {
    setShowTransformEntitiesSidebar(true);
  };

  const handleUploadFSClick = () => {
    setShowAddDocumentSidebar(true);
  };

  const extractFSNumber = (id: string): number => {
    const match = id.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const handleDeleteSpec = (specId: string) => {
    setSpecToDelete(specId);
    setShowDeleteModal(true);
  };

  const confirmDeleteSpec = async () => {
    if (!specToDelete) return;
    setIsDeleting(true);
    try {
      const spec = specs.find(s => s.id === specToDelete);
      if (spec && (spec as any)._firestoreId) {
        const docRef = doc(db, 'projects', projectId, 'fs', (spec as any)._firestoreId);
        await deleteDoc(docRef);
      }
      setSpecs(specs.filter(s => s.id !== specToDelete));
      showToast(t('project_tab_spec_deleted') || 'Spec deleted', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_spec_delete_failed') || 'Failed to delete spec', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSpecToDelete(null);
    }
  };

  const handleEntityClick = (entityId: string, entityType: 'URS' | 'TEST' | 'RISK' | 'SOP') => {
    onNavigateToEntity?.(entityId, entityType);
  };

  // Filter and sort specs based on search and status
  const filteredSpecs = useMemo(() => {
    const filtered = specs.filter(spec => {
      const matchesSearch = !searchValue || 
        spec.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        spec.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        spec.description.toLowerCase().includes(searchValue.toLowerCase());
      const matchesStatus = !statusFilter || spec.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    return filtered.sort((a, b) => {
      const numA = extractFSNumber(a.id);
      const numB = extractFSNumber(b.id);
      return numA - numB;
    });
  }, [specs, searchValue, statusFilter]);

  const tabs = [
    {
      label: t('project_tab_specifications') || 'Specifications',
      queryValue: 'specifications',
      content: (
        <div className={styles.specsSection}>
          {/* FilterBar */}
          <FilterBar
            filters={[
              {
                id: 'status',
                label: t('project_tab_status') || 'Status',
                options: [
                  { value: '', label: t('project_tab_all') || 'All' },
                  { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                  { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                  { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                  { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                ],
                value: statusFilter,
                onChange: (value) => setStatusFilter(value)
              }
            ]}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t('project_tab_search_specs') || 'Search specifications...'}
            showInviteButton={true}
            onAddUser={handleAddSpec}
            addButtonText={t('project_tab_add_spec') || 'Add Functional Spec'}
          />

          {loading ? null : filteredSpecs.length === 0 ? (
            <GlobalEmptyState
              buttonText={t('project_tab_add_spec') || 'Add Functional Spec'}
              buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
              onButtonClick={handleAddSpec}
            />
          ) : (
            <div className={styles.specsList}>
              {filteredSpecs.map((spec) => (
                <div key={spec.id} className={styles.specItem}>
                  <FunctionalSpecCard
                    spec={spec}
                    projectId={projectId}
                    onEdit={(spec) => {
                      setSelectedSpec(spec);
                      setShowSpecEditor(true);
                    }}
                    onDelete={handleDeleteSpec}
                    onViewTraceability={(spec) => {
                      showToast(t('project_tab_viewing_traceability') || 'Viewing traceability', 'info');
                    }}
                    onAiEnhance={(spec) => {
                      showToast(t('project_tab_ai_enhancing') || 'Enhancing with AI...', 'info');
                    }}
                    onEntityClick={handleEntityClick}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>

          {/* Add functional specification */}
          <SmallServiceCard
            id="add-spec"
            title={t('project_tab_add_spec') || 'Add Functional Spec'}
            description={t('project_tab_add_spec_desc') || 'Create a new functional specification'}
            fileTypeIcon=""
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
            buttonText={t('project_tab_add') || 'Add'}
            buttonOnClick={handleAddSpec}
          />

          {/* Read file with AI */}
          <SmallServiceCard
            id="read-file-with-ai-fs"
            title={t('project_tab_read_file_with_ai') || 'Datei mit KI einlesen'}
            description={t('project_tab_read_file_with_ai_fs_desc') || 'Importieren Sie Dateien um Anforderungen automatisch auslesen und erstellen zu lassen.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"}
            buttonText={t('project_tab_import') || 'Importieren'}
            buttonOnClick={handleUploadFSClick}
            disabled={!user}
          />

          {/* Generate with AI from ALL URS */}
          <SmallServiceCard
            id="ai-generate-fs"
            title={t('project_tab_ai_generate_from_urs') || 'AI Generate from ALL URS'}
            description={t('project_tab_ai_generate_from_urs_desc') || 'Generate functional specs from ALL URS using AI'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generate'}
            buttonOnClick={handleAiGenerateFromURS}
          />

          {/* Generate with AI from ALL FS */}
          <SmallServiceCard
            id="ai-generate-from-all-fs"
            title={t('project_tab_ai_generate_from_all_fs') || 'AI Generate from ALL FS'}
            description={t('project_tab_ai_generate_from_all_fs_desc') || 'Generate entities from ALL FS using AI'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generate'}
            buttonOnClick={handleAiGenerateFromFS}
          />

          {/* Transform Entities */}
          <SmallServiceCard
            id="transform-entities-fs"
            title={t('project_tab_transform_entities') || 'Transform Entities'}
            description={t('project_tab_transform_entities_desc') || 'Transform multiple entities using AI. Replace words, rephrase text, or update descriptions based on system changes.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_transform') || 'Transform'}
            buttonOnClick={handleTransformEntities}
          />

          {/* Export PDF */}
          <SmallServiceCard
            id="export-pdf-fs"
            title={t('project_tab_fs_export_pdf') || 'Als PDF exportieren'}
            description={t('project_tab_fs_export_pdf_desc') || 'Exportieren Sie alle Spezifikationen gesammelt als ein PDF Dokument.'}
            fileTypeIcon="/assets/graphics/pdf-icon.svg"
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />

          {/* Export CSV */}
          <SmallServiceCard
            id="export-csv-fs"
            title={t('project_tab_fs_export_csv') || 'Als CSV exportieren'}
            description={t('project_tab_fs_export_csv_desc') || 'Exportieren Sie alle Spezifikationen gesammelt als ein CSV Dokument.'}
            fileTypeIcon={"/assets/graphics/csv_icon.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={() => {
              try {
                if (specs.length === 0) {
                  showToast(t('project_tab_no_fs_to_export') || 'No specifications to export', 'warning');
                  return;
                }
                exportToCSV(specs, `FS_Export_${projectId}_${new Date().toISOString().split('T')[0]}`);
                showToast(t('project_tab_export_success') || 'Export successful', 'success');
              } catch (error) {
                console.error('Error exporting CSV:', error);
                showToast(t('project_tab_export_failed') || 'Export failed', 'error');
              }
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div className={styles.fsTab}>
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_page_tab_fs') || 'FS (Functional Specification)'}
        </h2>
      </div>

      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="specifications" />

      {/* Entity Generation Sidebar */}
      <EntityGenerationSidebar
        isOpen={showEntityGenerationSidebar}
        onClose={() => {
          setShowEntityGenerationSidebar(false);
          setSelectedEntitiesForGeneration([]);
        }}
        projectId={projectId}
        selectedEntities={selectedEntitiesForGeneration}
        onSuccess={() => {
          // Reload specs to show any new entities
          const loadSpecs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'fs');
              const snapshot = await getDocs(col);
              const loaded: FunctionalSpecification[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `FS-${doc.id.substring(0, 8)}`,
                } as FunctionalSpecification;
              });
              setSpecs(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading specs:', err);
              }
            }
          };
          loadSpecs();
        }}
      />

      {/* Transform Entities Sidebar */}
      <TransformEntitiesSidebar
        isOpen={showTransformEntitiesSidebar}
        onClose={() => {
          setShowTransformEntitiesSidebar(false);
        }}
        projectId={projectId}
        onSuccess={() => {
          // Reload specs to show transformed entities
          const loadSpecs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'fs');
              const snapshot = await getDocs(col);
              const loaded: FunctionalSpecification[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `FS-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as FunctionalSpecification & { _firestoreId: string };
              });
              setSpecs(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading specs:', err);
              }
            }
          };
          loadSpecs();
        }}
      />

      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_tab_delete_spec_title')?.replace('{id}', specToDelete || '') || `Delete Spec ${specToDelete}?`}
        paragraph={t('project_tab_delete_spec_paragraph') || 'Are you sure you want to delete this spec? This action cannot be undone.'}
        primaryLabel={t('global_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('global_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={confirmDeleteSpec}
        onSecondary={() => {
          setShowDeleteModal(false);
          setSpecToDelete(null);
        }}
        primaryDestructive
        loading={isDeleting}
      />

      {selectedSpec && (
        <SpecEditor
          projectId={projectId}
          spec={selectedSpec}
          onSave={async (spec) => {
            await handleSaveSpec(spec);
          }}
          onCancel={() => {
            setShowSpecEditor(false);
            setSelectedSpec(null);
          }}
          isOpen={showSpecEditor}
          isNew={!specs.some(s => s.id === selectedSpec.id)}
        />
      )}

      {/* Add Document Sidebar */}
      <AddDocumentSidebar
        isOpen={showAddDocumentSidebar}
        onClose={() => setShowAddDocumentSidebar(false)}
        projectId={projectId}
        defaultEntityTypes={['FS']}
        onSuccess={() => {
          // Reload specs after document upload
          const loadSpecs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'fs');
              const snapshot = await getDocs(col);
              const loaded: FunctionalSpecification[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `FS-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as FunctionalSpecification & { _firestoreId: string };
              });
              setSpecs(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading specs:', err);
              }
            }
          };
          loadSpecs();
        }}
      />
    </div>
  );
};

interface SpecEditorProps {
  projectId: string;
  spec: FunctionalSpecification;
  onSave: (spec: FunctionalSpecification) => Promise<void>;
  onCancel: () => void;
  isOpen: boolean;
  isNew: boolean;
}

const SpecEditor: React.FC<SpecEditorProps> = ({ projectId, spec, onSave, onCancel, isOpen, isNew }) => {
  const { t } = useLocale();
  const [editedSpec, setEditedSpec] = useState<FunctionalSpecification>(spec);

  if (!isOpen) return null;

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onCancel}>
      <div>
        <div className={styles.header}>
          <h2>
            {isNew
              ? (t('project_tab_fs_add') || 'Add FS')
              : (t('project_tab_fs_edit') || 'Edit FS')}
            <span className={styles.fsId}> {editedSpec.id}</span>
          </h2>
        </div>

        <form className={styles.form} onSubmit={async (e) => { e.preventDefault(); await onSave(editedSpec); }}>
          <div className={styles.modalTabs}>
            <GlobalTabs
              tabs={[
                {
                  label: t('project_tab_fs_details') || 'Details',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalInput
                        label={t('project_tab_title') || 'Title'}
                        value={editedSpec.title}
                        onChange={(e) => setEditedSpec({ ...editedSpec, title: e.target.value })}
                      />
                      <GlobalTextarea
                        label={t('project_tab_description') || 'Description'}
                        value={editedSpec.description}
                        onChange={(e) => setEditedSpec({ ...editedSpec, description: e.target.value })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_design_hint') || 'Design Hint'}
                        value={editedSpec.design_hint || ''}
                        onChange={(e) => setEditedSpec({ ...editedSpec, design_hint: e.target.value })}
                        rows={2}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_fs_classification') || 'Classification',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalDropdown
                        label={t('project_tab_category') || 'Category'}
                        value={editedSpec.category}
                        onChange={(value) => setEditedSpec({ ...editedSpec, category: value as any })}
                        options={[
                          { value: 'Application Function', label: t('project_tab_fs_category_application') || 'Application function' },
                          { value: 'Function', label: t('project_tab_fs_category_function') || 'Function' },
                          { value: 'Interface', label: t('project_tab_fs_category_interface') || 'Interface' },
                          { value: 'Report', label: t('project_tab_fs_category_report') || 'Report' },
                          { value: 'Infrastructure', label: t('project_tab_fs_category_infrastructure') || 'Infrastructure' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_gxp_relevance') || 'GxP Relevance'}
                        value={editedSpec.gxp_relevance}
                        onChange={(value) => setEditedSpec({ ...editedSpec, gxp_relevance: value as any })}
                        options={[
                          { value: 'None', label: t('project_tab_gxp_none') || 'None' },
                          { value: 'Low', label: t('project_tab_gxp_low') || 'Low' },
                          { value: 'Medium', label: t('project_tab_gxp_medium') || 'Medium' },
                          { value: 'High', label: t('project_tab_gxp_high') || 'High' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_status') || 'Status'}
                        value={editedSpec.status}
                        onChange={(value) => setEditedSpec({ ...editedSpec, status: value as any })}
                        options={[
                          { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                          { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                          { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                          { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                        ]}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_fs_behaviour') || 'Behaviour & I/O',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalTextarea
                        label={t('project_tab_inputs') || 'Inputs'}
                        value={(editedSpec.inputs || []).join('\n')}
                        onChange={(e) => setEditedSpec({ ...editedSpec, inputs: e.target.value.split('\n').filter(Boolean) })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_outputs') || 'Outputs'}
                        value={(editedSpec.outputs || []).join('\n')}
                        onChange={(e) => setEditedSpec({ ...editedSpec, outputs: e.target.value.split('\n').filter(Boolean) })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_preconditions') || 'Preconditions'}
                        value={(editedSpec.preconditions || []).join('\n')}
                        onChange={(e) => setEditedSpec({ ...editedSpec, preconditions: e.target.value.split('\n').filter(Boolean) })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_postconditions') || 'Postconditions'}
                        value={(editedSpec.postconditions || []).join('\n')}
                        onChange={(e) => setEditedSpec({ ...editedSpec, postconditions: e.target.value.split('\n').filter(Boolean) })}
                        rows={3}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_fs_links') || 'Connections',
                  content: (
                    <div className={styles.modalTabSection}>
                      <URSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_urs') || 'Linked URS'}
                        placeholder={t('project_tab_search_requirements') || 'Search URS…'}
                        selectedIds={editedSpec.links.urs_ids}
                        currentEntityId={editedSpec.id}
                        currentEntityType="FS"
                        onChange={(ids) =>
                          setEditedSpec({
                            ...editedSpec,
                            links: { ...editedSpec.links, urs_ids: ids }
                          })
                        }
                      />
                      <TestAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_tests') || 'Linked TEST'}
                        placeholder={t('project_tab_search_tests') || 'Search tests…'}
                        selectedIds={editedSpec.links.test_ids}
                        currentEntityId={editedSpec.id}
                        currentEntityType="FS"
                        onChange={(ids) =>
                          setEditedSpec({
                            ...editedSpec,
                            links: { ...editedSpec.links, test_ids: ids }
                          })
                        }
                      />
                      <RiskAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_risks') || 'Linked RISK'}
                        placeholder={t('project_tab_search_risks') || 'Search risks…'}
                        selectedIds={editedSpec.links.risk_ids}
                        currentEntityId={editedSpec.id}
                        currentEntityType="FS"
                        onChange={(ids) =>
                          setEditedSpec({
                            ...editedSpec,
                            links: { ...editedSpec.links, risk_ids: ids }
                          })
                        }
                      />
                      <SOPAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_sops') || 'Linked SOP'}
                        placeholder={t('project_tab_search_sops') || 'Search SOPs…'}
                        selectedIds={editedSpec.links.sop_ids}
                        currentEntityId={editedSpec.id}
                        currentEntityType="FS"
                        onChange={(ids) =>
                          setEditedSpec({
                            ...editedSpec,
                            links: { ...editedSpec.links, sop_ids: ids }
                          })
                        }
                      />
                    </div>
                  )
                }
              ]}
            />
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <GlobalButton
              type="button"
              onClick={onCancel}
              variant="secondary"
            >
              {t('project_tab_cancel') || 'Cancel'}
            </GlobalButton>
            <GlobalButton
              type="submit"
            >
              {t('project_tab_save') || 'Save'}
            </GlobalButton>
          </div>
        </form>
      </div>
    </GlobalSidebar>
  );
};

